import { isAxiosError } from "axios";
import { MutationCache, QueryClient } from "@tanstack/react-query";
import { isRouteErrorResponse } from "react-router-dom";

import { showToast } from "@/features/shared/components";

const shouldRetry = (failureCount: number, error: unknown) => {
  // If it's a 401, we don't retry. The user will be redirected to the login page.
  if (
    (isRouteErrorResponse(error) && error?.status === 401) ||
    (isAxiosError(error) && error.response?.status === 401)
  ) {
    return false;
  }

  // If it's a 403, we don't retry. The user will be redirected to the 404 page.
  if (
    (isAxiosError(error) && error.response?.status === 403) ||
    (isRouteErrorResponse(error) && error?.status === 403)
  ) {
    return false;
  }

  // If it's a 404, we don't retry. The user will be redirected to the 404 page.
  if (isAxiosError(error) && error.response?.status === 404) {
    return false;
  }

  // If we have failed more than 3 times, we don't retry.
  if (failureCount >= 3) {
    return false;
  }

  return true;
};

export function initQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 100000,
        retry: shouldRetry,
        throwOnError: (error) => {
          if (
            (isAxiosError(error) && error.response?.status === 401) ||
            (isAxiosError(error) && error.response?.status === 403)
          ) {
            return true;
          }

          return isRouteErrorResponse(error);
        },
      },
    },
    mutationCache: new MutationCache({
      onError: (error) => {
        // Global error handler for mutations.
        if (isAxiosError(error)) {
          showToast(error.message, { type: "error" });
        }
      },
    }),
  });
}
